import React, { useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useParams,
} from 'react-router-dom';

import Switch from '@mui/material/Switch';
import { Upload, Button, Spin } from 'antd'; // <-- IMPORT Spin
import { UploadOutlined } from '@ant-design/icons';

import Result from './Result';

const school_mapping = {
  UIUC: 'canvas.illinois.edu',
  aristai: 'aristai.instucture.com',
};

function App() {
  const [videoLinks, setVideoLinks] = useState('');
  const [isToggled, setIsToggled] = useState(false);

  const [audioTranscriptFiles, setAudioTranscriptFiles] = useState([]);
  const [videoTranscriptFiles, setVideoTranscriptFiles] = useState([]);
  const [preferredVideoLanguages, setPreferredVideoLanguages] = useState(['English']);

  const { school, course_code, canvas_id } = useParams();
  const navigate = useNavigate();

  // Language selection
  const availableLanguages = [
    'English',
    'Spanish',
    'French',
    'German',
    'Chinese',
    'Japanese',
    'Korean',
  ];
  const [selectedLanguages, setSelectedLanguages] = useState(['English']);

  // Transcript type
  const [transcriptType, setTranscriptType] = useState({
    concise: true,
    verbose: true,
  });

  // Audio voice
  const [audioVoice, setAudioVoice] = useState({
    male: true,
    female: true,
  });

  // NEW: Spinner state
  const [loading, setLoading] = useState(false);

  // Advanced settings
  const handleTranscriptTypeChange = (e) => {
    const { name, checked } = e.target;
    setTranscriptType((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleAudioVoiceChange = (e) => {
    const { name, checked } = e.target;
    setAudioVoice((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  function generateUniqueFileName(originalName) {
    const timestamp = Date.now();
    const nameParts = originalName.split('.');
    const extension = nameParts.pop();
    const baseName = nameParts.join('.');
    return `${baseName}_${timestamp}.${extension}`;
  }

  const handlePreferredVideoLanguageClick = (lang) => {
    setPreferredVideoLanguages((prevSelected) => {
      if (lang === 'English') {
        // English must always remain selected
        return prevSelected;
      } else if (prevSelected.includes(lang)) {
        return prevSelected.filter((l) => l !== lang);
      } else {
        return [...prevSelected, lang];
      }
    });
  };

  // Helper: get presigned URL
  async function getPresignedUrl(fileName) {
    const response = await fetch('https://be.aristai.io/api/video_a11y/get_presigned_url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileName }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to get presigned URL');
    }
    const data = await response.json();
    return data.uploadURL;
  }

  // Handlers
  const handleToggle = (event) => {
    setIsToggled(event.target.checked);
  };

  const handleVideoLinksChange = (e) => {
    setVideoLinks(e.target.value);
  };

  // Ant Design Upload handlers
  const handleAudioTranscriptBeforeUpload = async (file) => {
    try {
      const originalName = file.name;
      const uniqueFileName = generateUniqueFileName(originalName);
  
      // Get presigned URL using the unique filename
      const uploadURL = await getPresignedUrl(uniqueFileName);
      await fetch(uploadURL, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: file,
      });
      const s3Link = uploadURL.split('?')[0];
  
      // Determine a display name that differentiates duplicates
      setAudioTranscriptFiles((prev) => {
        let displayName = originalName;
        const duplicateCount = prev.filter(f => f.name === originalName).length;
        if (duplicateCount > 0) {
          displayName = `${originalName} (${duplicateCount + 1})`;
        }
  
        return [
          ...prev,
          {
            uid: file.uid,
            name: displayName,          // Use differentiated display name
            uploadName: uniqueFileName, // Unique name for backend reference if needed
            url: s3Link,
            status: 'done',
            percent: 100,
          },
        ];
      });
    } catch (error) {
      console.error('Error uploading audio transcript:', error);
    }
    return false;
  };

  const handleAudioTranscriptRemove = (file) => {
    setAudioTranscriptFiles((prev) => prev.filter((f) => f.uid !== file.uid));
  };

  const handleVideoTranscriptBeforeUpload = async (file) => {
    try {
      const originalName = file.name;
      const uniqueFileName = generateUniqueFileName(originalName);
  
      const uploadURL = await getPresignedUrl(uniqueFileName);
      await fetch(uploadURL, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: file,
      });
      const s3Link = uploadURL.split('?')[0];
  
      setVideoTranscriptFiles((prev) => {
        let displayName = originalName;
        const duplicateCount = prev.filter(f => f.name === originalName).length;
        if (duplicateCount > 0) {
          displayName = `${originalName} (${duplicateCount + 1})`;
        }
  
        return [
          ...prev,
          {
            uid: file.uid,
            name: displayName,
            uploadName: uniqueFileName,
            url: s3Link,
            status: 'done',
            percent: 100,
          },
        ];
      });
    } catch (error) {
      console.error('Error uploading video transcript:', error);
    }
    return false;
  };

  const handleVideoTranscriptRemove = (file) => {
    setVideoTranscriptFiles((prev) => prev.filter((f) => f.uid !== file.uid));
  };

  // Toggle language selection
  const handleLanguageClick = (lang) => {
    setSelectedLanguages((prevSelected) => {
      if (lang === 'English') {
        return prevSelected;
      } else if (prevSelected.includes(lang)) {
        return prevSelected.filter((l) => l !== lang);
      } else {
        return [...prevSelected, lang];
      }
    });
  };

  // Main "Generate" action
  const handleGenerate = () => {
    // 1) Validate user has chosen at least one transcript type
    if (!transcriptType.concise && !transcriptType.verbose) {
      alert('Please select at least one Transcript Type: Concise or Verbose.');
      return;
    }

    // 2) Validate user has chosen at least one audio voice
    if (!audioVoice.male && !audioVoice.female) {
      alert('Please select at least one Audio Voice: Male or Female.');
      return;
    }

    // 3) Turn on spinner
    setLoading(true);

    // Prepare request
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');

    const raw = JSON.stringify({
      body: {
        Course_Code_Input: course_code,
        Course_Name_Input: 'Video_Accessibility',
        School_Input: school,
        Course_Language_Input: 'English',
        canvas_course_code: canvas_id,
        URL_Input: videoLinks,
        canvas_base_domain: school_mapping[school],
        language_selection_list: selectedLanguages.filter(
          (lang) => lang !== 'English'
        ),
        audio_transcript_s3_urls: audioTranscriptFiles.map((f) => f.url),
        video_transcript_s3_urls: videoTranscriptFiles.map((f) => f.url),
        // If needed, also pass `preferredVideoLanguages` here:
        // preferred_video_languages: preferredVideoLanguages,
      },
    });

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow',
    };

    fetch('https://ldrioriogp2kdlvq2m6x2z5zzm0gvitt.lambda-url.us-east-1.on.aws/', requestOptions)
      .then((response) => response.text())
      .then((result) => {
        // Go to result page
        navigate(`/${school}/${course_code}/${canvas_id}/result`);
      })
      .catch((error) => console.error(error))
      .finally(() => {
        // Turn off spinner
        setLoading(false);
      });
  };

  return (
    // Wrap main body in a Spin so it covers everything while loading is true
      <div className="main-body">
        {/* Top Bar */}
        <div className="top-bar">
          <img className="logo" src="/logo.svg" alt="logo" />
          <button
            className="back-button"
            onClick={() => {
              navigate(`/${school}/${course_code}/${canvas_id}/result`);
            }}
          >
            Go to history
          </button>
        </div>

        {/* Page Content */}
        <div className="content">
          <h1>Video Accessibility Enhancement</h1>
          <p className="description">
            Get an Accessible Video Package with videos, multilingual transcripts,
            and resources to meet media accessibility standards.
          </p>

          <div className="section">
            {/* Toggle for CSV Upload */}
            <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center' }}>
              <Switch
                checked={isToggled}
                onChange={handleToggle}
                color="primary"
                inputProps={{ 'aria-label': 'Upload CSV toggle' }}
              />
              <span style={{ marginLeft: '0.75rem', fontWeight: 'bold' }}>
                Upload your edited transcript CSV file(s)
              </span>
            </div>

            {/* If NOT toggled, show video URL input */}
            {!isToggled && (
              <>
                <label className="section-label-text" htmlFor="videoLinks">
                  <img
                    src="/video_link.svg"
                    alt="video link icon"
                    className="video_link_logo"
                  />
                  Video Links
                  <span className="tooltip-container">
                    <span className="tooltip-icon">i</span>
                    <span className="tooltip-content">
                      <img
                        src="/Tooltip.svg"
                        alt="Tooltip"
                        className="tooltip-image"
                      />
                    </span>
                  </span>
                </label>
                <textarea
                  id="videoLinks"
                  placeholder="Enter video URL (one per line, press shift + enter for a new line)"
                  value={videoLinks}
                  onChange={handleVideoLinksChange}
                  rows={4}
                  style={{ width: '100%', marginTop: '1rem' }}
                />
              </>
            )}

            {/* If toggled, show Ant Design Upload components */}
            {isToggled && (
              <div style={{ marginTop: '1rem' }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label className="section-label">
                    <strong>Audio Transcript CSV Files:</strong>
                  </label>
                  <Upload
                    multiple
                    accept=".csv"
                    beforeUpload={handleAudioTranscriptBeforeUpload}
                    onRemove={handleAudioTranscriptRemove}
                    fileList={audioTranscriptFiles}
                  >
                    <Button icon={<UploadOutlined />} style={{ marginTop: '0.5rem' }} className="custom-upload-button" >
                      Upload Audio Transcript CSV
                    </Button>
                  </Upload>
                </div>

                <div>
                  <label className="section-label">
                    <strong>Video Transcript CSV Files:</strong>
                  </label>
                  <Upload
                    multiple
                    accept=".csv"
                    beforeUpload={handleVideoTranscriptBeforeUpload}
                    onRemove={handleVideoTranscriptRemove}
                    fileList={videoTranscriptFiles}
                  >
                    <Button icon={<UploadOutlined />} style={{ marginTop: '0.5rem' }} className="custom-upload-button">
                      Upload Video Transcript CSV
                    </Button>
                  </Upload>
                </div>
              </div>
            )}
          </div>

          {/* Language Selection */}
          <div className="section">
            <div className="advanced-settings-header">
              <label className="section-label" htmlFor="videoLinks">
                <img
                  src="/language.svg"
                  alt="language icon"
                  className="video_link_logo"
                />
                Language Selection
              </label>
              <p style={{ fontStyle: 'italic' }}>
                Selecting more languages will increase generation time
              </p>
            </div>

            <p>
              Select your preferred language(s) for accurate and accessible transcripts.
            </p>
            <div className="language-buttons">
              {availableLanguages.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  className={selectedLanguages.includes(lang) ? 'active' : ''}
                  onClick={() => handleLanguageClick(lang)}
                  style={{
                    marginRight: '8px',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  disabled={lang === 'English'} // "English" always selected
                >
                  {selectedLanguages.includes(lang) ? (
                    <>
                      <img
                        src={`/select.svg`}
                        alt="select icon"
                        style={{ width: '16px', height: '16px', marginRight: '4px' }}
                      />
                      {lang}
                    </>
                  ) : (
                    lang
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Advanced Settings */}
          <div className="section">
            <h2>Advanced Settings</h2>

            {/* Transcript type */}
            <div className="adv-subsection">
              <h3>Transcript type</h3>
              <label>
                <input
                  type="checkbox"
                  name="concise"
                  checked={transcriptType.concise}
                  onChange={handleTranscriptTypeChange}
                  style={{
                    transform: 'scale(1.3)',
                    marginRight: '8px',
                    transformOrigin: 'top left',
                  }}
                />
                Concise
              </label>
              <label style={{ marginLeft: '50px' }}>
                <input
                  type="checkbox"
                  name="verbose"
                  checked={transcriptType.verbose}
                  onChange={handleTranscriptTypeChange}
                  style={{
                    transform: 'scale(1.3)',
                    marginRight: '8px',
                    transformOrigin: 'top left',
                  }}
                />
                Verbose
              </label>
            </div>

            {/* Audio voice */}
            <div className="adv-subsection">
              <h3>Audio voice</h3>
              <label>
                <input
                  type="checkbox"
                  name="male"
                  checked={audioVoice.male}
                  onChange={handleAudioVoiceChange}
                  style={{
                    transform: 'scale(1.3)',
                    marginRight: '8px',
                    transformOrigin: 'top left',
                  }}
                />
                Male Voice
              </label>
              <label style={{ marginLeft: '30px' }}>
                <input
                  type="checkbox"
                  name="female"
                  checked={audioVoice.female}
                  onChange={handleAudioVoiceChange}
                  style={{
                    transform: 'scale(1.3)',
                    marginRight: '8px',
                    transformOrigin: 'top left',
                  }}
                />
                Female Voice
              </label>
            </div>

            {/* Preferred Video Languages */}
            <div className="adv-subsection">
              <h3>Preferred Video Languages</h3>
              <p style={{ fontStyle: 'italic' }}>
                Choose a subset from your selected languages
              </p>
              <div className="language-buttons">
                {selectedLanguages.map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    className={preferredVideoLanguages.includes(lang) ? 'active' : ''}
                    onClick={() => handlePreferredVideoLanguageClick(lang)}
                    style={{
                      marginRight: '8px',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    disabled={lang === 'English'}
                  >
                    {preferredVideoLanguages.includes(lang) ? (
                      <>
                        <img
                          src={`/select.svg`}
                          alt="select icon"
                          style={{ width: '16px', height: '16px', marginRight: '4px' }}
                        />
                        {lang}
                      </>
                    ) : (
                      lang
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button className="generate-button"
            onClick={handleGenerate}
            disabled={loading}   // disable button while loading to prevent duplicates
            style={{
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? (
              // Option A: "Loading..." text
              <div className="spinner" />
              
              // Option B: Show a spinner icon
              // <div className="spinner" />
            ) : (
              'Generate Accessible Video Package'
            )}
          </button>
        </div>
      </div>
  );
}

function AppWrapper() {
  return (
    <Router>
      <Routes>
        <Route path="/:school/:course_code/:canvas_id" element={<App />} />
        <Route
          path="/:school/:course_code/:canvas_id/result"
          element={<Result />}
        />
      </Routes>
    </Router>
  );
}

export default AppWrapper;
